<template>
  <div class="input-wrapper" :class="{'labeled': hasLabelSlot || showInputLabel, cssClass, 'has-error': errorMessage}">
    <slot v-if="hasLabelSlot" name="label"></slot>
    <label v-else-if="showInputLabel" :class="['label', {'disabled': disabled}, {'required': required}]">
      {{ label ?? name }}
    </label>
    <sl-input ref="inputControl"
      v-click-outside:[clickOutsideExcludedCssClass]="handleClickOutside"
      :class="{'clearable': clearable}" 
      :placeholder="placeholder" 
      :value="changedValue" 
      :maxlength="maxlength" 
      :disabled="disabled" 
      :clearable="clearable" 
      @keydown.enter="handleInputKeydown"
      @keydown.escape="handleInputKeydown"
      @sl-input="handleValueInput" 
      @sl-change="handleValueChange" 
      @sl-clear="handleValueClear"
    >
      <span slot="clear-icon" class="icon-close-cross-exit-quit-delete"></span>
    </sl-input>
    <p class="error-message" v-show="errorMessage">{{ errorMessage }}</p>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs, useSlots, watch } from 'vue';
import _ from 'lodash';
import "@shoelace-style/shoelace/dist/components/input/input.js";
import { SlInput } from '@shoelace-style/shoelace';

interface IMcClearableInputProps {
  clearable?: boolean;
  clickOutsideExcludedCssClass?: string;
  cssClass?: string;
  disabled?: boolean;
  errorMessage?: string;
  inputValue?: string;
  label?: string;
  maxlength?: number;
  name?: string;
  placeholder?: string;
  required?: boolean;
  showLabel?: boolean;
}

const props = withDefaults(defineProps<IMcClearableInputProps>(), {
  clearable: true,
  clickOutsideExcludedCssClass: "",
  cssClass: "",
  disabled: false,
  inputValue: "",
  maxlength: 250,
  placeholder: "Please enter...",
  required: false,
  showLabel: true,
});

const {
  clearable,
  clickOutsideExcludedCssClass,
  cssClass,
  disabled,
  errorMessage,
  inputValue,
  label,
  maxlength,
  name,
  placeholder,
  required,
  showLabel
} = toRefs(props);

const slots = useSlots() as ReturnType<typeof useSlots>;
const hasLabelSlot = computed(() => !!slots.label);
const showInputLabel = computed(() => showLabel.value && (label.value || name.value));

const inputControl = ref<SlInput | undefined>(undefined);
const changedValue = ref<string|undefined>(_.cloneDeep(inputValue.value));

const emit = defineEmits([
  'input',
  'change',
  'clear',
  'click:outside',
  'press:enter',
  'press:escape',
]);

const handleValueInput = (event: Event) => {
  setChangedValue(event, false);
  emit('input', changedValue.value);
};

const handleValueChange = (event: Event) => {
  setChangedValue(event);
  emit('change', changedValue.value);
};

const handleValueClear = () => {
  changedValue.value = undefined;
  emit('clear', changedValue.value);
};

const handleClickOutside = () => {
  if (disabled.value) return;
  emit('click:outside');
};

const handleInputKeydown = (event: KeyboardEvent) => {
  if (disabled.value) return;

  const eventKey = event.key.toLowerCase();

  switch (eventKey) {
    case 'enter':
      event.preventDefault();
      emit('press:enter');
      break;
    case 'escape':
      event.preventDefault();
      emit('press:escape');
      break;
    default:
      break;
  }
};

const setChangedValue = (event: Event, trimValue: boolean = true) => {
  const target = event?.target as HTMLInputElement;
  const targetValue = trimValue ? target.value?.trim() : target.value;
  changedValue.value = targetValue ? targetValue : undefined;
};

const selectValue = () => {
  if (inputControl.value) {
    inputControl.value.select();
  }
};

defineExpose({
  selectValue,
});

watch(
  () => inputValue.value,
  () => changedValue.value = _.cloneDeep(inputValue.value)
);
</script>

<style>
:root {
  --clearable-input-color: var(--colour-text-neutral-default);
  --clearable-input-placeholder-color: var(--colour-text-disabled);
  --clearable-input-icon-color: var(--colour-icons-contrast-dark);
  --clearable-input-background-color-disabled: var(--colour-background-disabled);
  --clearable-input-border-color-disabled: var(--colour-border-neutral-subtle);
  --clearable-input-color-disabled: var(--colour-text-disabled);
  /* label */
  --clearable-input-label-font-color: var(--colour-text-neutral-subtle);
  --clearable-input-label-font-color-disabled: var(--colour-text-disabled);
  --clearable-input-label-font-size: var(--font-size-body-small);
  --clearable-input-label-font-style: normal;
  --clearable-input-label-font-weight: var(--font-weight-body-medium);
  --clearable-input-label-line-height: var(--line-height-xxx-s);
  --clearable-input-label-required-icon-color: var(--colour-background-brand-solid);
  --clearable-input-label-required-icon-font-size: 12px;
  --clearable-input-label-required-icon-font-weight: 600;
  --clearable-input-label-required-icon-margin: 0 0 0 2px;
}
</style>

<style lang="scss" scoped>
  sl-input {
    --sl-input-background-color-disabled: var(--clearable-input-background-color-disabled);
    --sl-input-border-color-disabled: var(--clearable-input-border-color-disabled);
    --sl-input-color-disabled: var(--clearable-input-color-disabled);
    --sl-input-color: var(--colour-text-neutral-default);

    &::part(input) {
      color: var(--clearable-input-color);
      padding: 8px;

      &::placeholder {
        color: var(--clearable-input-placeholder-color);
      }
    }

    &::part(clear-button){
      width: initial;
    }
  }

  sl-input.clearable {
    &::part(input) {
      padding-right: 0;
    }
  }

  .icon-close-cross-exit-quit-delete {
    font-size: 25px;
    cursor: pointer;
    margin: 0;
    color: var(--clearable-input-icon-color);
  }

  .label {
    color: var(--clearable-input-label-font-color);
    font-size: var(--clearable-input-label-font-size);
    font-style: var(--clearable-input-label-font-style);
    font-weight: var(--clearable-input-label-font-weight);
    line-height: var(--clearable-input-label-line-height);

    &.disabled {
      color: var(--clearable-input-label-font-color-disabled);
    }


    &.required {
      &::after {
        content: "*";
        color: var(--clearable-input-label-required-icon-color);
        font-size: var(--clearable-input-label-required-icon-font-size);
        font-weight: var(--clearable-input-label-required-icon-font-weight);
        margin: var(--clearable-input-label-required-icon-margin);
      }
    }
  }
</style>